import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, onSnapshot, collectionGroup, query, where, doc, updateDoc, deleteDoc, getDocs, setDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { COUNTRY_CODES } from '../constants';
import { useOrchestration } from '../hooks/useOrchestration';
import { useDataIntegrity } from '../hooks/useDataIntegrity';
import { useMonitoring } from '../hooks/useMonitoring';
import { useGlobalLoad } from '../hooks/useGlobalLoad';
import ReactCountryFlag from 'react-country-flag';

function generateRoomCode() {
  return [...Array(6)].map(() => 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)]).join('');
}

const MonitorDashboard = ({ user, contests }) => {
  const { activeCount: globalActive } = useGlobalLoad(true);
  const reactionMode = globalActive >= 100 ? 'degraded' : 'global';
  const [rooms, setRooms] = useState([]);
  const [allPresence, setAllPresence] = useState({});
  const [allMembers, setAllMembers] = useState({});
  const [allScores, setAllScores] = useState({});
  const [allReactionCounts, setAllReactionCounts] = useState({});
  const [activityFeed, setActivityFeed] = useState([]);
  const [selectedContestId, setSelectedContestId] = useState(
    contests.find(c => c.id === 'euro2026sf2')?.id || contests.find(c => c.id?.startsWith('euro2026'))?.id || contests[0]?.id || ''
  );

  // Room management state
  const [expandedRoomId, setExpandedRoomId] = useState(null);
  const [createRoomName, setCreateRoomName] = useState('');
  const [createContestId, setCreateContestId] = useState(
    contests.find(c => c.id === 'euro2026sf2')?.id || contests[0]?.id || ''
  );
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [renamingRoomId, setRenamingRoomId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingRoomId, setDeletingRoomId] = useState(null);

  const activeContest = contests.find(c => c.id === selectedContestId);
  const { contest, currentAct, totalActs, isOrchestrating, timeElapsed, countdown,
    nextAct, prevAct, pauseAuto, resumeAuto, startOrchestration, closeContest } = useOrchestration(selectedContestId);

  const { errors: monitoringErrors } = useMonitoring();

  const flatMembers = useMemo(() => Object.values(allMembers).flat(), [allMembers]);
  const flatPresence = useMemo(() => Object.values(allPresence).flat(), [allPresence]);

  const integrityChecks = useDataIntegrity(
    allScores, activeContest?.contestants || [], flatMembers, flatPresence
  );

  // Load rooms list
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'rooms'), (snap) => {
      setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Track which rooms have completed their initial snapshot so we don't
  // replay all historical docs as JOIN/REACT events when the listener
  // re-attaches after a room-list change.
  const initialLoadedRef = useRef({ members: new Set(), reactions: new Set() });

  const feedRef = useRef([]);
  const addToFeed = useCallback((type, message, roomId) => {
    const entry = { type, message, roomId, time: new Date() };
    feedRef.current = [entry, ...feedRef.current].slice(0, 100);
    setActivityFeed([...feedRef.current]);
  }, []);

  // Subscribe to members + presence + reactions for each room
  useEffect(() => {
    const unsubs = [];
    rooms.forEach(room => {
      unsubs.push(onSnapshot(collection(db, 'rooms', room.id, 'members'), (snap) => {
        const members = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllMembers(prev => ({ ...prev, [room.id]: members }));
        const isInitial = !initialLoadedRef.current.members.has(room.id);
        if (isInitial) {
          initialLoadedRef.current.members.add(room.id);
          return;
        }
        snap.docChanges().forEach(change => {
          if (change.type === 'added') {
            const data = change.doc.data();
            addToFeed('JOIN', `${data.name} joined ${room.name}`, room.id);
          }
        });
      }));

      unsubs.push(onSnapshot(collection(db, 'rooms', room.id, 'presence'), (snap) => {
        setAllPresence(prev => ({
          ...prev,
          [room.id]: snap.docs.map(d => ({ id: d.id, ...d.data() })),
        }));
      }));

      unsubs.push(onSnapshot(collection(db, 'rooms', room.id, 'reactions'), (snap) => {
        const now = Date.now();
        const recent = snap.docs.filter(d => {
          const ts = d.data().timestamp?.toDate?.();
          return ts && (now - ts.getTime() < 60000);
        });
        setAllReactionCounts(prev => ({ ...prev, [room.id]: recent.length }));
        const isInitial = !initialLoadedRef.current.reactions.has(room.id);
        if (isInitial) {
          initialLoadedRef.current.reactions.add(room.id);
          return;
        }
        snap.docChanges().forEach(change => {
          if (change.type === 'added') {
            const data = change.doc.data();
            addToFeed('REACT', `${data.userName} ${data.emoji}`, room.id);
          }
        });
      }));
    });
    return () => unsubs.forEach(u => u());
  }, [rooms, addToFeed]);

  // Subscribe to scores via collection group
  useEffect(() => {
    if (!selectedContestId) return;
    const scoresQuery = query(
      collectionGroup(db, 'scores'),
      where('contestId', '==', selectedContestId)
    );
    const unsub = onSnapshot(scoresQuery, (snap) => {
      const nextScores = {};
      snap.forEach(sDoc => {
        const data = sDoc.data();
        const contestantId = sDoc.ref.parent.parent.id;
        if (!nextScores[contestantId]) nextScores[contestantId] = {};
        nextScores[contestantId][sDoc.id] = data;
      });
      setAllScores(nextScores);
      snap.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          const data = change.doc.data();
          if (data.overall !== undefined) {
            const contestantId = change.doc.ref.parent.parent.id;
            const contestant = activeContest?.contestants?.find(c => String(c.id) === String(contestantId));
            const name = contestant?.country || contestant?.name || contestantId;
            addToFeed('VOTE', `${data.voterName} scored ${name} ${data.overall.toFixed(1)}`, null);
          }
        }
      });
    });
    return unsub;
  }, [selectedContestId, activeContest, addToFeed]);

  // Add monitoring errors to activity feed
  useEffect(() => {
    monitoringErrors.forEach(err => {
      addToFeed('ERROR', `${err.userName || 'Unknown'}: ${err.message}`, err.roomId);
    });
  }, [monitoringErrors, addToFeed]);

  // Derived metrics
  const totalActiveUsers = useMemo(() => {
    const now = Date.now();
    const allP = Object.values(allPresence).flat();
    return allP.filter(p => {
      const ts = p.lastActive?.toDate?.() ? p.lastActive.toDate().getTime() : 0;
      return now - ts < 240000;
    }).length;
  }, [allPresence]);

  const totalVotes = useMemo(() => {
    return Object.values(allScores).reduce((sum, cs) => sum + Object.keys(cs).length, 0);
  }, [allScores]);

  const totalPossibleVotes = useMemo(() => {
    const totalMembers = Object.values(allMembers).reduce((sum, m) => sum + m.length, 0);
    return totalMembers * (activeContest?.contestants?.length || 0);
  }, [allMembers, activeContest]);

  const reactionsPerMin = useMemo(() => {
    return Object.values(allReactionCounts).reduce((sum, c) => sum + c, 0);
  }, [allReactionCounts]);

  const failCount = integrityChecks.filter(c => c.status === 'fail').length;
  const warnCount = integrityChecks.filter(c => c.status === 'warn').length;
  const systemStatus = failCount > 0 || monitoringErrors.length > 5 ? 'red'
    : warnCount > 0 || monitoringErrors.length > 0 ? 'yellow' : 'green';

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const statusColors = { green: '#22c55e', yellow: '#fbbf24', red: '#ef4444' };

  // Admin create — bypasses useRooms limits/date locks so SF2/Final can be prepped
  const handleCreateRoom = async () => {
    if (!createRoomName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const code = generateRoomCode();
      const roomRef = doc(collection(db, 'rooms'));
      await setDoc(roomRef, {
        name: createRoomName.trim(),
        code,
        contestId: createContestId,
        createdBy: user.id,
        createdAt: new Date().toISOString(),
      });
      await setDoc(doc(db, 'rooms', roomRef.id, 'members', user.id), {
        name: user.name,
        photoURL: user.photoURL || null,
        joinedAt: new Date().toISOString(),
      });
      await setDoc(doc(db, 'users', user.id), { roomIds: arrayUnion(roomRef.id) }, { merge: true });
      setCreateRoomName('');
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRenameRoom = async (roomId) => {
    if (!renameValue.trim()) { setRenamingRoomId(null); return; }
    try {
      await updateDoc(doc(db, 'rooms', roomId), { name: renameValue.trim() });
      setRenamingRoomId(null);
    } catch (e) {
      alert(`Failed to rename room: ${e.message}`);
    }
  };

  const handleDeleteRoom = async (room) => {
    if (!window.confirm(`Delete room "${room.name}"? This cannot be undone.`)) return;
    setDeletingRoomId(room.id);
    try {
      const membersSnap = await getDocs(collection(db, 'rooms', room.id, 'members'));
      const memberIds = membersSnap.docs.map(d => d.id);
      await Promise.all(membersSnap.docs.map(d => deleteDoc(d.ref)));
      await Promise.all(memberIds.map(uid =>
        setDoc(doc(db, 'users', uid), { roomIds: arrayRemove(room.id) }, { merge: true })
      ));
      await deleteDoc(doc(db, 'rooms', room.id));
      if (expandedRoomId === room.id) setExpandedRoomId(null);
    } catch (e) {
      alert(`Failed to delete room: ${e.message}`);
    } finally {
      setDeletingRoomId(null);
    }
  };

  return (
    <div style={{ background: '#0a0a0a', color: '#e0e0e0', minHeight: '100vh', fontFamily: '"Inter", system-ui, sans-serif' }}>

      {/* Contest selector */}
      <div style={{ padding: '8px 12px', background: '#111', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontFamily: '"Unbounded Variable", sans-serif', fontWeight: 700, fontSize: '14px', color: '#ff2d87', letterSpacing: '1px' }}>
          MISSION CONTROL
        </span>
        <select
          value={selectedContestId}
          onChange={(e) => setSelectedContestId(e.target.value)}
          style={{ background: '#1a1a1a', color: '#fff', border: '1px solid #333', padding: '4px 8px', fontSize: '12px' }}
        >
          {contests.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Status Bar */}
      <div className="monitor-status-bar">
        <StatusTile
          label="System" value={systemStatus === 'green' ? 'HEALTHY' : systemStatus === 'yellow' ? 'WARNINGS' : 'ERRORS'}
          color={statusColors[systemStatus]}
          bg={systemStatus === 'green' ? '#0d2a0d' : systemStatus === 'yellow' ? '#2a2a0d' : '#2a0d0d'}
        />
        <StatusTile label="Active Users" value={totalActiveUsers} sub={`/ ${rooms.length} rooms`} color="#7c3aed" />
        <StatusTile label="Votes Cast" value={totalVotes} sub={`/ ${totalPossibleVotes} possible`} color="#06b6d4" />
        <StatusTile label="Reactions/min" value={reactionsPerMin} color="#fbbf24" />
        <StatusTile
          label="Errors" value={monitoringErrors.length}
          color={monitoringErrors.length === 0 ? '#22c55e' : monitoringErrors.length <= 5 ? '#fbbf24' : '#ef4444'}
        />
        <StatusTile
          label="Global Active" value={globalActive} sub={reactionMode.toUpperCase()}
          color={globalActive < 75 ? '#22c55e' : globalActive < 100 ? '#fbbf24' : '#ef4444'}
          bg={globalActive < 75 ? '#0d2a0d' : globalActive < 100 ? '#2a2a0d' : '#2a0d0d'}
        />
      </div>

      {/* Now Playing & Controls */}
      <div style={{ padding: '12px', background: '#0f0f0f', borderBottom: '2px solid #222' }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#ff2d87', marginBottom: '8px' }}>
          Now Playing
        </div>
        {isOrchestrating && currentAct ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {(() => {
                const contestant = activeContest?.contestants?.find(c => String(c.id) === String(currentAct.contestantId));
                const country = contestant?.country;
                const cc = country ? COUNTRY_CODES[country] : null;
                return cc ? <ReactCountryFlag countryCode={cc} svg style={{ width: '28px', height: '20px' }} /> : null;
              })()}
              <span style={{ fontSize: '20px', fontWeight: 700, fontFamily: '"Unbounded Variable", sans-serif' }}>
                {(() => {
                  const contestant = activeContest?.contestants?.find(c => String(c.id) === String(currentAct.contestantId));
                  return contestant?.name || currentAct.contestantId;
                })()}
              </span>
              <span style={{ color: '#888', fontSize: '14px' }}>
                {currentAct.order} / {totalActs}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', fontFamily: '"JetBrains Mono", monospace' }}>
              <span style={{ color: '#06b6d4' }}>ELAPSED {formatTime(timeElapsed)}</span>
              {countdown !== null && <span style={{ color: '#fbbf24' }}>NEXT IN {formatTime(countdown)}</span>}
              <span style={{
                padding: '2px 8px', fontSize: '10px', fontWeight: 700, letterSpacing: '1px',
                background: contest?.orchestrationMode === 'auto' ? '#0d2a0d' : '#2a1a0d',
                color: contest?.orchestrationMode === 'auto' ? '#22c55e' : '#fbbf24',
                border: `1px solid ${contest?.orchestrationMode === 'auto' ? '#22c55e' : '#fbbf24'}`,
              }}>
                {contest?.orchestrationMode === 'auto' ? 'AUTO' : 'MANUAL'}
              </span>
            </div>
            <div className="monitor-now-playing-controls">
              <PanicButton onClick={prevAct} label="PREV" color="#7c3aed" />
              <PanicButton onClick={nextAct} label="NEXT" color="#7c3aed" />
              {contest?.orchestrationMode === 'auto'
                ? <PanicButton onClick={pauseAuto} label="PAUSE AUTO" color="#fbbf24" />
                : <PanicButton onClick={resumeAuto} label="RESUME AUTO" color="#22c55e" />
              }
              <PanicButton onClick={closeContest} label="CLOSE CONTEST" color="#ef4444" />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#666' }}>
              {contest?.closed ? 'Contest is closed' : 'Orchestration not started'}
            </span>
            {!contest?.closed && contest?.runningOrder?.length > 0 && (
              <PanicButton onClick={startOrchestration} label="START SHOW" color="#22c55e" />
            )}
            {!contest?.runningOrder?.length && (
              <span style={{ color: '#fbbf24', fontSize: '12px' }}>No running order configured</span>
            )}
          </div>
        )}
      </div>

      {/* Main grid: 2 columns → 1 column on mobile */}
      <div className="monitor-grid">

        {/* Live Activity Feed */}
        <Panel title="Live Activity Feed" color="#7c3aed">
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {activityFeed.length === 0 && <div style={{ color: '#444', textAlign: 'center', padding: '20px' }}>Waiting for activity...</div>}
            {activityFeed.map((entry, i) => (
              <div key={`${entry.type}-${entry.time.getTime()}-${i}`} style={{ fontSize: '12px', lineHeight: '2', fontFamily: '"JetBrains Mono", monospace' }}>
                <span style={{ color: '#666' }}>
                  {entry.time.toLocaleTimeString('en-US', { hour12: false })}
                </span>{' '}
                <span style={{ color: feedColors[entry.type] || '#888' }}>{entry.type}</span>{' '}
                {entry.message}
              </div>
            ))}
          </div>
        </Panel>

        {/* Data Integrity */}
        <Panel title="Data Integrity Checks" color="#ff2d87">
          {integrityChecks.map(check => (
            <div key={check.id} style={{ fontSize: '12px', lineHeight: '2.2', display: 'flex', justifyContent: 'space-between' }}>
              <span>
                <span style={{ color: check.status === 'pass' ? '#22c55e' : check.status === 'warn' ? '#fbbf24' : '#ef4444' }}>
                  {check.status === 'pass' ? '✔' : check.status === 'warn' ? '⚠' : '✘'}
                </span>{' '}
                <span style={{ color: check.status !== 'pass' ? (check.status === 'warn' ? '#fbbf24' : '#ef4444') : '#e0e0e0' }}>
                  {check.label}
                </span>
              </span>
              <span style={{ color: '#666' }}>{check.detail}</span>
            </div>
          ))}
        </Panel>

        {/* Rooms */}
        <Panel title="Rooms" color="#06b6d4">
          {/* Create room form */}
          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #222' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <input
                value={createRoomName}
                onChange={e => setCreateRoomName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateRoom()}
                placeholder="Room name"
                style={{ flex: '1 1 120px', background: '#1a1a1a', color: '#fff', border: '1px solid #333', padding: '4px 8px', fontSize: '12px', fontFamily: '"JetBrains Mono", monospace', minWidth: 0 }}
              />
              <select
                value={createContestId}
                onChange={e => setCreateContestId(e.target.value)}
                style={{ flex: '0 0 auto', background: '#1a1a1a', color: '#fff', border: '1px solid #333', padding: '4px 8px', fontSize: '12px' }}
              >
                {contests.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <PanicButton onClick={handleCreateRoom} label={creating ? '...' : 'CREATE'} color="#06b6d4" />
            </div>
            {createError && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>{createError}</div>}
          </div>

          {rooms.length === 0 && <div style={{ color: '#444', textAlign: 'center', padding: '20px' }}>No rooms</div>}
          {rooms.map(room => {
            const members = allMembers[room.id] || [];
            const roomPresence = allPresence[room.id] || [];
            const now = Date.now();
            const activeCount = roomPresence.filter(p => {
              const ts = p.lastActive?.toDate?.() ? p.lastActive.toDate().getTime() : 0;
              return now - ts < 240000;
            }).length;
            const totalContestants = contests.find(c => c.id === room.contestId)?.contestants?.length || 0;
            const roomContestantIds = new Set(
              (contests.find(c => c.id === room.contestId)?.contestants || []).map(c => String(c.id))
            );
            const votedMemberCount = members.filter(m =>
              Object.entries(allScores).some(([contestantId, cs]) =>
                roomContestantIds.has(contestantId) && cs[m.id] !== undefined
              )
            ).length;
            const isExpanded = expandedRoomId === room.id;
            const isDeleting = deletingRoomId === room.id;
            const isRenaming = renamingRoomId === room.id;

            return (
              <div key={room.id} style={{ borderBottom: '1px solid #1a1a1a', marginBottom: '2px' }}>
                {/* Row header */}
                <div
                  onClick={() => !isRenaming && setExpandedRoomId(isExpanded ? null : room.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <span style={{ color: activeCount > 0 ? '#22c55e' : '#555', flexShrink: 0 }}>{'●'}</span>
                    {isRenaming ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRenameRoom(room.id); if (e.key === 'Escape') setRenamingRoomId(null); }}
                        onClick={e => e.stopPropagation()}
                        style={{ background: '#1a1a1a', color: '#fff', border: '1px solid #06b6d4', padding: '2px 6px', fontSize: '12px', fontFamily: '"JetBrains Mono", monospace', width: '140px' }}
                      />
                    ) : (
                      <span style={{ color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>{room.name}</span>
                    )}
                    <span style={{ color: '#444', flexShrink: 0 }}>({room.code})</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, fontSize: '11px' }}>
                    <span style={{ color: '#fff' }}>{members.length}m</span>
                    <span style={{ color: '#22c55e' }}>{activeCount} live</span>
                    <span style={{ color: '#06b6d4' }}>{votedMemberCount}/{members.length} voted</span>
                    <span style={{ color: isExpanded ? '#06b6d4' : '#444', fontSize: '10px' }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ background: '#0a0a0a', padding: '10px 8px', marginBottom: '4px' }}>
                    {/* Member list */}
                    {members.length === 0
                      ? <div style={{ color: '#444', fontSize: '11px', marginBottom: '8px' }}>No members</div>
                      : members.map(m => {
                          const memberVotes = Object.entries(allScores).filter(([contestantId, cs]) =>
                            roomContestantIds.has(contestantId) && cs[m.id] !== undefined
                          ).length;
                          const presenceEntry = roomPresence.find(p => p.id === m.id);
                          const isActive = presenceEntry
                            ? (Date.now() - (presenceEntry.lastActive?.toDate?.()?.getTime?.() || 0)) < 240000
                            : false;
                          return (
                            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px', fontFamily: '"JetBrains Mono", monospace', borderBottom: '1px solid #111' }}>
                              <span style={{ color: isActive ? '#e0e0e0' : '#555' }}>
                                <span style={{ color: isActive ? '#22c55e' : '#333', marginRight: '6px' }}>{'●'}</span>
                                {m.name}
                              </span>
                              <span style={{ color: memberVotes > 0 ? '#06b6d4' : '#444' }}>
                                {memberVotes}/{totalContestants} votes
                              </span>
                            </div>
                          );
                        })
                    }
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {isRenaming ? (
                        <>
                          <PanicButton onClick={() => handleRenameRoom(room.id)} label="SAVE" color="#22c55e" />
                          <PanicButton onClick={() => setRenamingRoomId(null)} label="CANCEL" color="#888" />
                        </>
                      ) : (
                        <PanicButton onClick={() => { setRenamingRoomId(room.id); setRenameValue(room.name); }} label="RENAME" color="#fbbf24" />
                      )}
                      <PanicButton
                        onClick={() => handleDeleteRoom(room)}
                        label={isDeleting ? '...' : 'DELETE'}
                        color="#ef4444"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </Panel>

        {/* Error Log */}
        <Panel title="Error Log" color="#fbbf24" badge={monitoringErrors.length === 0 ? 'CLEAR' : null}>
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {monitoringErrors.length === 0 && (
              <div style={{ color: '#444', textAlign: 'center', padding: '20px' }}>No errors captured</div>
            )}
            {monitoringErrors.map(err => (
              <div key={err.id} style={{ fontSize: '12px', lineHeight: '2', fontFamily: '"JetBrains Mono", monospace' }}>
                <span style={{ color: '#666' }}>
                  {err.timestamp?.toDate?.().toLocaleTimeString('en-US', { hour12: false }) || '—'}
                </span>{' '}
                <span style={{ color: '#ef4444' }}>[{err.category}]</span>{' '}
                <span style={{ color: '#fbbf24' }}>{err.userName || 'Unknown'}</span>{' '}
                {err.message}
              </div>
            ))}
          </div>
          <div style={{ fontSize: '10px', color: '#444', marginTop: '8px' }}>
            Captures: failed writes, auth errors, listener drops, unhandled exceptions from all connected users
          </div>
        </Panel>
      </div>

      {/* Bottom: Contest state */}
      <div style={{ padding: '12px 16px', background: '#0f0f0f', borderTop: '1px solid #222', fontSize: '12px' }}>
        <span style={{ color: '#888' }}>Contest:</span>{' '}
        <span style={{ color: '#fff' }}>{activeContest?.name || 'None selected'}</span>
        {' · '}
        <span style={{ color: contest?.closed ? '#ef4444' : '#22c55e' }}>
          {contest?.closed ? 'VOTING CLOSED' : 'VOTING OPEN'}
        </span>
        {' · '}
        <span style={{ color: '#888' }}>
          {Object.keys(allScores).length}/{activeContest?.contestants?.length || 0} contestants scored
        </span>
      </div>
    </div>
  );
};

// --- Sub-components ---

const feedColors = {
  VOTE: '#06b6d4',
  REACT: '#fbbf24',
  JOIN: '#22c55e',
  LEAVE: '#888',
  ADMIN: '#ff2d87',
  ERROR: '#ef4444',
};

const StatusTile = ({ label, value, sub, color, bg }) => (
  <div style={{
    flex: 1, padding: '12px 16px', background: bg || '#1a1a2e',
    borderLeft: `3px solid ${color}`, fontSize: '13px',
  }}>
    <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</div>
    <div style={{ color: typeof value === 'string' && value.includes('ERROR') ? '#ef4444' : (color || '#fff'), fontSize: '20px', fontWeight: 700, marginTop: '2px', fontFamily: '"JetBrains Mono", monospace' }}>
      {value} {sub && <span style={{ color: '#888', fontSize: '12px', fontWeight: 400 }}>{sub}</span>}
    </div>
  </div>
);

const PanicButton = ({ onClick, label, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '6px 12px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px',
      background: 'transparent', color, border: `1px solid ${color}`,
      cursor: 'pointer', textTransform: 'uppercase', fontFamily: '"JetBrains Mono", monospace',
    }}
    onMouseOver={(e) => { e.target.style.background = color; e.target.style.color = '#000'; }}
    onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.color = color; }}
  >
    {label}
  </button>
);

const Panel = ({ title, color, badge, children }) => (
  <div style={{ background: '#0f0f0f', padding: '16px' }}>
    <div style={{
      fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color,
      marginBottom: '12px', borderBottom: '1px solid #222', paddingBottom: '6px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      {title}
      {badge && <span style={{ background: '#0d2a0d', color: '#22c55e', padding: '2px 8px', fontSize: '10px' }}>{badge}</span>}
    </div>
    {children}
  </div>
);

export default MonitorDashboard;
